/**
 * TradingStatusContext — manages the kill switch state globally.
 *
 * Fetches trading_enabled from the API on mount and exposes a toggle method.
 * Used by KillSwitch (Header) and TradingPausedBanner (Layout).
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import tradingStatusService from '../services/tradingStatusService';

interface TradingStatusContextType {
  tradingEnabled: boolean;
  loading: boolean;
  setTradingEnabled: (enabled: boolean) => void;
}

const TradingStatusContext = createContext<TradingStatusContextType>({
  tradingEnabled: true,
  loading: true,
  setTradingEnabled: () => {},
});

export const useTradingStatus = () => useContext(TradingStatusContext);

export const TradingStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [tradingEnabled, setTradingEnabledState] = useState(true);
  const [loading, setLoading] = useState(true);

  // Fetch on auth
  useEffect(() => {
    if (!isAuthenticated) {
      setTradingEnabledState(true);
      setLoading(false);
      return;
    }
    tradingStatusService.getStatus()
      .then(setTradingEnabledState)
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const setTradingEnabled = useCallback((enabled: boolean) => {
    setTradingEnabledState(enabled);
  }, []);

  return (
    <TradingStatusContext.Provider value={{ tradingEnabled, loading, setTradingEnabled }}>
      {children}
    </TradingStatusContext.Provider>
  );
};
